/**
 * @accounts/shared — the contract surface both /server and /web bind to.
 *
 * This file is TYPE-ONLY BY DESIGN. The package is exported as source
 * (`"exports": { ".": "./src/index.ts" }`), so a runtime export here would force a
 * build step or a bundler transpile inside node_modules on every consumer. Keeping it
 * type-only means every import erases at compile time and nothing has to be built.
 *
 * The introspection contract types land here in Phase 2. That shape gates every
 * consuming app's integration, so it is versioned deliberately — changing it after a
 * consuming app depends on it is a suite-wide breaking change, never a refactor.
 */

/** Canonical error codes. Every non-2xx response carries exactly one of these. */
export type ApiErrorCode =
  | 'validation_error'
  | 'unauthenticated'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'internal_error'
  | 'invalid_credentials'
  | 'password_change_required'
  | 'password_unchanged'
  | 'rate_limited'
  | 'immutable_field'
  | 'last_active_admin'
  | 'inactive_target'
  /**
   * The target account is locked by the operator latch. Deactivation and role changes are
   * refused with this code; password reset and 2FA reset remain available (anti-lockout).
   * The message always points at the operator CLI, the only place the latch is cleared.
   */
  | 'account_locked'
  /*
   * The three caller-credential failures on POST /api/introspect — all 401, all about the
   * CALLING APPLICATION's credential, never about a user or a session. There is
   * deliberately no `inactive_application` error code: a deactivated application still
   * authenticates and receives a truthful 200 `application_inactive` VERDICT (the
   * off-switch is a verdict input, not a caller-auth failure — operator ruling 2026-08-07).
   */
  /** No such application key, or the credential headers were missing/malformed. */
  | 'unknown_application'
  /** The key exists but the presented secret matches no credential ever issued for it. */
  | 'invalid_app_credential'
  /** The presented secret matches a credential that has since been revoked — rotate. */
  | 'revoked_app_credential'
  /**
   * The audit export's matching set exceeds the server-side row cap. The message names
   * both numbers and says how to narrow — never a silent truncation, never a bare code.
   */
  | 'export_too_large';

/**
 * The credential lanes this service can issue. Every credential-bearing cookie is
 * structurally self-describing from birth: its payload begins with one of these values,
 * and every read path declares which lane(s) it accepts — a cookie carrying any other
 * lane (or none) is refused as malformed, never defaulted to the full session.
 *
 *   - 'session' — the full signed-in session. The only lane a consuming application ever
 *     sees as valid: introspection accepts exactly this one.
 *   - 'password-change-pending' — issued at login INSTEAD of 'session' when the account
 *     holds a temporary password (`must_change_password`). Accepted only by the three
 *     `authenticated-open` self-service routes (see who you are, change your password,
 *     leave); refused structurally everywhere else, introspection included, so a
 *     temporary-password holder can never enter a consuming application.
 *   - '2fa-pending' — issued at login INSTEAD of 'session' when the account has TOTP
 *     enabled (the password verified, the second factor has not), or when the operator's
 *     2FA requirement holds an unenrolled account at enrollment. Accepted by exactly two
 *     gates: 'totp-challenge' (POST /api/auth/2fa/verify and …/recovery — the lane's
 *     EXCLUSIVE reach; a full session is refused there) and 'totp-enroll' (POST
 *     /api/auth/2fa/enroll and …/confirm, shared with the full session for self-service
 *     enrollment). Refused structurally everywhere else, introspection included
 *     (`wrong_lane`), so a half-authenticated person can never reach a full-session
 *     surface or enter a consuming application.
 *
 * Every lane carries `session_version` and is subject to `users.active` — a bump or a
 * deactivation kills any of them on the very next request. A future short-lived
 * intermediate lane (enrollment-pending, …) is a one-line addition to this union — the
 * payload shape and the lane gate already exist, so adding a lane is never a credential
 * event for existing users.
 */
export type SessionLane = 'session' | 'password-change-pending' | '2fa-pending';

/**
 * Why a presented session credential was refused. Emitted ONLY when the cookie unsigned
 * validly, parsed, and named a real user — i.e. only to someone who held a session this
 * service itself issued. Every other 401 (no cookie, bad signature, malformed payload,
 * wrong lane, unknown user id) carries NO reason and stays byte-for-byte uniform — a
 * wrong-lane cookie is a STRUCTURAL rejection, not a session-state fact.
 */
export type SessionRefusalReason =
  /** The account was deactivated. */
  | 'account_inactive'
  /**
   * This credential was ended from somewhere that is not the device holding it: an
   * administrator signed the user out (everywhere, or this one device), or the person
   * signed this device out from another one (per-device sign-out, "sign out everywhere
   * else"). One member covers all of those on purpose — to the refused device they are
   * the same fact, and the union is switched exhaustively by /web.
   */
  | 'signed_out_elsewhere'
  /** The password was reset or changed, ending every prior session. */
  | 'password_changed'
  /** The session ended: it expired, or the specific cause could not be determined. */
  | 'session_ended'
  /**
   * The suite session is valid but the ADMINISTRATOR ELEVATION cookie (host-only, issued
   * alongside the session at sign-in for admin/HR/IT accounts — D-H1) is missing, stale or
   * belongs to another session. Recovery is always the same: sign in again. Also what an
   * account promoted to a privileged role mid-session sees until it signs in again.
   */
  | 'elevation_required';

/**
 * The one error envelope every non-2xx response uses.
 * `reason` is additive and appears only on the session-refusal 401s described above;
 * `code` stays `unauthenticated` in every such case.
 */
export interface ApiErrorBody {
  error: { code: ApiErrorCode; message: string; reason?: SessionRefusalReason };
}

/* -------------------------------------------------------------------------- *
 * Phase 1 resource shapes.
 *
 * Every timestamp is an ISO-8601 string, never a Date — JSON has no Date, and a
 * shape that pretends otherwise lies to one of its two consumers.
 * -------------------------------------------------------------------------- */

/** A user as the admin panel sees one. Never carries a hash, a token, or a password. */
export interface UserPublic {
  id: string;
  email: string;
  name: string;
  isAdmin: boolean;
  /**
   * The HR actor type (SIR-7460 decision 1): Accounts-internal, like `isAdmin`. Shown in
   * the admin users shape only; NEVER part of the introspection payload.
   */
  isHr: boolean;
  /**
   * The IT actor type (SIR-7720): separate from HR so its access can be narrowed later
   * without a data migration. Accounts-internal and never part of introspection.
   */
  isIt: boolean;
  active: boolean;
  mustChangePassword: boolean;
  /**
   * 2FA STATUS, and ONLY status (operator ruling, Pass D). Structurally never a
   * secret-shaped field: no TOTP secret (even encrypted), no replay counter, no recovery
   * codes — those are internal state and stay internal. The operator lock latch is
   * likewise deliberately absent from this shape.
   */
  twoFactorEnabled: boolean;
  /** ISO timestamp of when enrollment confirmed; null when 2FA is not enabled. */
  twoFactorEnrolledAt: string | null;
  /**
   * ISO timestamp of the most recent 2FA reset (admin endpoint or operator CLI — both
   * stamp through the ONE reset service), cleared by a successful re-enrollment. Lets an
   * administrator tell "never set up" (enabled false, this null) from "was reset and
   * needs to re-enrol" (enabled false, this set). Status metadata only — never
   * secret-shaped.
   */
  twoFactorResetAt: string | null;
  /** DISPLAY-ONLY directory metadata. Never a security input, never an authorization key. */
  title: string | null;
  /** DISPLAY-ONLY directory metadata. Never a security input, never an authorization key. */
  department: string | null;
  /** Live sessions right now. The admin Users pane renders this as the Sessions row label. */
  activeSessionCount: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * The signed-in principal, as returned by login, every 2FA completion and `GET /api/auth/me`.
 * One shape at every one of those endpoints (built by the server's `toSessionUser`).
 */
export interface SessionUser {
  id: string;
  email: string;
  name: string;
  isAdmin: boolean;
  /**
   * The HR actor type (SIR-7460 decision 1): the client derives the HR surface from it.
   * Affordance only; the server enforces the `user-manager` gate. Accounts-internal: never
   * part of the introspection payload.
   */
  isHr: boolean;
  /** Accounts-internal IT actor type. Affordance only; the server enforces every gate. */
  isIt: boolean;
  /** Server-resolved Access visibility for this session's role. Admin always has all three. */
  access: RoleAccessSwitches;
  mustChangePassword: boolean;
}

export interface RoleAccessSwitches {
  seeMatrix: boolean;
  /** Effective only when seeMatrix is also true. */
  exportMatrix: boolean;
  seeCatalog: boolean;
}

export interface AccessSwitchesPublic {
  hr: RoleAccessSwitches;
  it: RoleAccessSwitches;
}

/**
 * One ACTIVE application credential, as the admin panel sees it. There is deliberately no
 * secret field of any kind: the secret is bcrypt-hashed at rest and unrecoverable by design
 * (D12), returned exactly once at issue time and never again by any surface.
 */
export interface ApplicationCredentialPublic {
  id: string;
  createdAt: string;
  /** When this credential last authenticated a real introspection call; null if never. */
  lastUsedAt: string | null;
}

/**
 * An application in the suite. `hasSecret` is derived from the credential table —
 * the secret itself is unrecoverable by design, so only its existence is exposed.
 */
export interface ApplicationPublic {
  id: string;
  key: string;
  name: string;
  baseUrl: string;
  active: boolean;
  hasSecret: boolean;
  /** ACTIVE credentials only. Revoked rows stay in the table forever but are not listed. */
  credentials: ApplicationCredentialPublic[];
  lastStatus: string | null;
  lastVersion: string | null;
  lastCheckedAt: string | null;
  /** Launcher tile copy; null = none. Empty string stores as null at the write boundary. */
  description: string | null;
  /** The two tag slots, by immutable id. Null = slot empty. Never the same tag twice. */
  tag1Id: string | null;
  tag2Id: string | null;
  /**
   * SLOT-ORDERED resolved tags (tag1 then tag2), nulls skipped — resolved by FK JOIN so a
   * rename shows immediately. `retiredAt` lets the panel flag a tag that is still carried
   * but retired (it stays on applications already carrying it; only NEW assignment refuses).
   */
  tags: { id: string; name: string; retiredAt: string | null }[];
  /**
   * `/api/admin/applications/<id>/logo?v=<token>` when a logo is set, otherwise null —
   * the ADMIN serving route; the launcher shape carries its own grant-gated URL. The token
   * derives from the row's updated-at (the branding `?v=` cache-busting law).
   */
  logoUrl: string | null;
  /**
   * The APPLICATION'S PILL COLOUR — a swatch name from ANNOUNCEMENT_SWATCHES, the colour
   * of this application's chip wherever one renders (launcher announcements, the Sessions
   * screens). A first-class column on the application row (`applications.pill_color`),
   * edited on the application's own detail pane. Null = no colour assigned = rendered
   * with no colour, never a guessed default.
   */
  pillColor: AnnouncementSwatch | null;
  createdAt: string;
  updatedAt: string;
}

/** Who may enter which application. Revoked (`revokedAt`), never deleted. */
export interface GrantPublic {
  id: string;
  userId: string;
  applicationId: string;
  grantedBy: string;
  grantedAt: string;
  revokedAt: string | null;
  user: { id: string; email: string; name: string };
  application: { id: string; key: string; name: string };
}

/** The envelope every paginated list uses. */
export interface Paginated<T> {
  data: T[];
  meta: { total: number; limit: number; offset: number };
}

/* -------------------------------------------------------------------------- *
 * Collection-wide summaries.
 *
 * TWO DENOMINATORS, kept explicitly separate so neither can be mistaken for the
 * other:
 *   - `meta.total`   — rows matching the CURRENT query filters; pairs with
 *                      `limit`/`offset` for pagination.
 *   - `meta.summary` — UNFILTERED, collection-wide counts, regardless of any
 *                      `q`/`active` filter, so a header stays a stable summary of
 *                      the whole directory rather than shifting as somebody types.
 * Summaries are computed in SQL over the whole table — never derived from the
 * returned page, which is capped and therefore lies for large collections.
 * -------------------------------------------------------------------------- */

/** Collection-wide user counts. `admins` counts users who are active AND is_admin — the last-active-admin guard's own definition of an administrator. */
export interface UsersSummary {
  active: number;
  deactivated: number;
  admins: number;
}

/**
 * Collection-wide application counts. The three health counts partition the
 * applications whose `lastStatus` is set; an application never checked
 * (`lastStatus` null) counts toward none of them.
 */
export interface ApplicationsSummary {
  active: number;
  healthy: number;
  maintenance: number;
  unreachable: number;
}

/** Collection-wide grant counts: live (`revokedAt` null) vs revoked. */
export interface GrantsSummary {
  live: number;
  revoked: number;
}

/** A paginated list whose meta additionally carries collection-wide summary counts. */
export interface PaginatedWithSummary<T, S> extends Paginated<T> {
  meta: Paginated<T>['meta'] & { summary: S };
}

/* --------------------------------- auth ---------------------------------- */

export interface LoginRequest {
  email: string;
  password: string;
  /** Where to land after login. Validated SERVER-SIDE against registered app origins. */
  redirect?: string;
}

/**
 * A login that issued a FULL session (`session` lane) or the password-change wall
 * (`password-change-pending` lane): the signed-in user rides along.
 */
export interface LoginCompleteResponse {
  user: SessionUser;
  /**
   * An ABSOLUTE url to a registered ACTIVE application, decided SERVER-SIDE, or `null`
   * meaning "land inside Accounts" — the client then derives which internal surface from
   * its own one route declaration. Never a relative path: this field says "leave", or
   * says nothing.
   */
  redirectTo: string | null;
  twoFactor?: undefined;
}

/**
 * A login where the password verified but a SECOND FACTOR stands between it and a full
 * session — the cookie issued alongside is the '2fa-pending' lane, not 'session'.
 *   - 'challenge': TOTP is enabled; submit a code (POST /api/auth/2fa/verify) or a
 *     recovery code (POST /api/auth/2fa/recovery).
 *   - 'enroll': the operator requires 2FA for this account and it is not enrolled yet;
 *     complete enrollment (POST /api/auth/2fa/enroll → …/confirm).
 * NO `user` here (audit L2): nothing about the account — not its name, not its roles, not
 * its access switches — is disclosed until the second factor is proven. The challenge and
 * enrollment responses carry the user once they succeed.
 */
export interface LoginPendingTwoFactorResponse {
  twoFactor: 'challenge' | 'enroll';
  /** Same law as `LoginCompleteResponse.redirectTo`. */
  redirectTo: string | null;
  user?: undefined;
}

export type LoginResponse = LoginCompleteResponse | LoginPendingTwoFactorResponse;

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  /**
   * A post-login destination the person was heading to when the must-change-password wall
   * intercepted. RE-VALIDATED server-side on arrival, exactly as at login — it reaches
   * this endpoint through the client, so it is untrusted input, not a carried verdict.
   */
  redirect?: string;
}

export interface ChangePasswordResponse {
  ok: true;
  /** Same law as `LoginResponse.redirectTo`: an absolute validated URL, or null. */
  redirectTo: string | null;
}

/* ------------------------------- 2FA (TOTP) ------------------------------- */

/**
 * The operator's 2FA requirement — enforced SERVER-SIDE at login:
 *   - 'optional'        — nobody is forced; enrolled accounts are still challenged.
 *   - 'required_admins' — administrators and HR without TOTP are held at the enrollment step.
 *   - 'required_all'    — everyone without TOTP is held at the enrollment step.
 * A held login receives the '2fa-pending' lane and `twoFactor: 'enroll'`; a full session
 * is unreachable until enrollment confirms. Shipping default: 'optional' (unseeded).
 */
export type TotpRequirement = 'optional' | 'required_admins' | 'required_all';

/** GET /api/auth/me/2fa — the signed-in person's own 2FA state. Never anyone else's. */
export interface TwoFactorStatusResponse {
  enabled: boolean;
  /** ISO timestamp of when enrollment confirmed; null when not enabled. */
  enrolledAt: string | null;
  /** Spendable (unused, unsuperseded) recovery codes remaining. */
  recoveryCodesRemaining: number;
  /** A secret has been issued (enrollment begun) but no code has confirmed it yet. */
  pendingEnrollment: boolean;
}

/**
 * POST /api/auth/2fa/enroll. THE ONE SANCTIONED SURFACE for the plaintext TOTP secret:
 * this response, once. At rest it exists only AES-256-GCM-encrypted (D12). Re-beginning
 * replaces the pending secret; refused (409 conflict) once 2FA is enabled.
 */
export interface TwoFactorEnrollBeginResponse {
  /** The shared secret, base32 (RFC 4648), for manual entry into an authenticator. */
  secret: string;
  /** `otpauth://totp/<issuer>:<email>?secret=…&issuer=…&algorithm=SHA1&digits=6&period=30`
   *  — the string the QR pane encodes. Issuer comes from branding, never hardcoded. */
  otpauthUri: string;
}

export interface TwoFactorEnrollConfirmRequest {
  /** The 6-digit code currently showing in the authenticator. */
  code: string;
  /** Same law as ChangePasswordRequest.redirect: untrusted, re-validated server-side. */
  redirect?: string;
}

/**
 * Confirm flips 2FA live and hands the browser its next credential in the same response:
 * a full 'session' cookie, or 'password-change-pending' when the temporary-password wall
 * still stands (`user.mustChangePassword` says which — same client contract as login).
 */
export interface TwoFactorEnrollConfirmResponse {
  user: SessionUser;
  redirectTo: string | null;
  /**
   * THE ONE SANCTIONED SURFACE for plaintext recovery codes: this response, once. Ten
   * single-use codes, stored only as bcrypt hashes, never shown again by any endpoint.
   */
  recoveryCodes: string[];
}

/** POST /api/auth/2fa/verify — the login challenge. Requires the '2fa-pending' lane. */
export interface TwoFactorVerifyRequest {
  /** The 6-digit TOTP code. */
  code: string;
  /** Untrusted, re-validated server-side against the live application allow-list. */
  redirect?: string;
}
/** The response is a LoginResponse: user + server-validated redirectTo (no `twoFactor`). */

/** POST /api/auth/2fa/recovery — sign in with a single-use recovery code instead. */
export interface TwoFactorRecoveryRequest {
  code: string;
  redirect?: string;
}

export interface TwoFactorRecoveryResponse {
  user: SessionUser;
  redirectTo: string | null;
  /** Spendable codes left AFTER this one was consumed — the client's "N codes remain" nudge. */
  recoveryCodesRemaining: number;
}

/**
 * POST /api/auth/me/2fa/disable. Proof is the account PASSWORD — the first factor, which
 * a hijacked session does not carry, and which survives a lost authenticator (the main
 * reason 2FA gets turned off). Idempotent: disabling when nothing is enrolled is `ok`.
 */
export interface TwoFactorDisableRequest {
  password: string;
}

export interface TwoFactorDisableResponse {
  ok: true;
}

/**
 * POST /api/auth/me/2fa/recovery-codes — regenerate. Same password proof as disable.
 * The old batch is superseded (retired, never deleted); refused (409) when no TOTP
 * state exists to attach codes to.
 */
export interface TwoFactorRegenerateRecoveryCodesRequest {
  password: string;
}

export interface TwoFactorRegenerateRecoveryCodesResponse {
  /** THE ONE SANCTIONED SURFACE for this batch's plaintext, exactly once. */
  recoveryCodes: string[];
}

/* ------------------------------- launcher -------------------------------- */

/**
 * One application as the launcher shows it: an ACTIVE application the signed-in person
 * holds a live (non-revoked) grant for. There is deliberately no `active` field — an
 * inactive application never appears here at all — and no `hasSecret`, which is
 * administrative information a launcher has no business carrying.
 */
export interface LauncherApplication {
  id: string;
  key: string;
  name: string;
  /** Absolute http(s) URL — where "launch" sends the browser. */
  baseUrl: string;
  /**
   * Self-reported by the application: 'healthy' | 'maintenance' | 'unreachable'.
   * `null` means NEVER CHECKED, which is not a fault and makes no status claim at all.
   * Nothing writes these fields until the Phase 3 poller.
   */
  lastStatus: string | null;
  lastVersion: string | null;
  lastCheckedAt: string | null;
  /** Tile copy set by an administrator; null = no description. */
  description: string | null;
  /**
   * 0, 1 or 2 entries in SLOT ORDER (tag1 then tag2), nulls skipped. RESOLVED BY FK JOIN
   * at read time so a tag rename shows immediately; a retired tag still resolves and still
   * renders. Tags are NEUTRAL labels — no colour, ever, by ruling.
   */
  tags: { id: string; name: string }[];
  /**
   * `/api/me/applications/<id>/logo?v=<token>` when a logo is set, otherwise null. The
   * token derives from the application row's updated-at (the branding `?v=` law), so a
   * replaced logo changes the URL and the browser re-requests. The serving route enforces
   * the SAME grant law as this list: no live grant on an active application = uniform 404.
   */
  logoUrl: string | null;
}

/**
 * The launcher layout preference. PER-USER and SERVER-SIDE by operator ruling, so it
 * follows the person across browsers — deliberately NOT where the palette/mode preference
 * lives (localStorage, per-device, stays there: the sign-in page renders for visitors with
 * no user row). A runtime const (AUDIT_GROUPS precedent) so the server's zod enum and this
 * type can never drift.
 */
export const LAUNCHER_LAYOUTS = ['tiles', 'list'] as const;

export type LauncherLayout = (typeof LAUNCHER_LAYOUTS)[number];

/**
 * THE ONE HOME of the shipped launcher-layout default — the value a person sees before
 * they have ever chosen, and the value `users.launcher_layout` NULL resolves to.
 *
 * It lives HERE rather than server-side because it has genuinely TWO consumers that must
 * never disagree: the server resolves NULL through it on the launcher read, and /web needs
 * the same answer for its FIRST PAINT, before that read has landed — a client guessing a
 * different default would flip the whole page's layout the moment the response arrived.
 * A second copy in /web would be a second writable home for one fact, so there is one.
 */
export const DEFAULT_LAUNCHER_LAYOUT: LauncherLayout = 'list';

/**
 * GET /api/me/applications. Unpaginated: a person's granted set is inherently small.
 * `layout` is the RESOLVED value — the user's stored preference, or the shipped default
 * when unset — derived server-side; no client ever re-derives it.
 */
export interface LauncherApplicationsResponse {
  data: LauncherApplication[];
  layout: LauncherLayout;
}

/**
 * Body of PUT /api/me/preferences. Writes the SESSION'S OWN user row only — there is no
 * userId parameter and there never will be one. Deliberately unaudited: a personal display
 * preference, not a privileged mutation.
 */
export interface UpdateMyPreferencesRequest {
  launcherLayout: LauncherLayout;
}

/** The stored preference as it now stands — the round-trip proof. */
export interface MyPreferencesResponse {
  launcherLayout: LauncherLayout;
}

/* -------------------------------- sessions -------------------------------- */

/**
 * One application a session has actually been seen entering.
 *
 * `color` is the application's pill colour (`applications.pill_color`, ApplicationPublic's
 * `pillColor`), resolved by the SAME id join that already supplies `id` and `name` — the
 * chip renders it on both session surfaces. It discloses nothing new: every entry here is
 * an application THIS session has itself entered, so the caller already holds its id and
 * name; a colour on that same entry adds no existence signal. Null = no colour assigned =
 * an uncoloured chip, never a guessed default.
 */
export interface SessionAppSeen {
  id: string;
  name: string;
  color: AnnouncementSwatch | null;
  lastSeenAt: string;
}

/** One device with a live credential. Identical shape on both surfaces. */
export interface SessionEntry {
  id: string;
  /** The User-Agent parse, or 'Unknown device' — never a guess. */
  deviceLabel: string;
  /** True when the browser did not identify itself; drives the honest row treatment. */
  deviceUnknown: boolean;
  /** The source ADDRESS, recorded at issuance — always `request.ip`, never a header re-derive. */
  sourceAddress: string | null;
  /**
   * City and country resolved ONCE from the source address at write time, from a local
   * offline database — never re-resolved on read, and never an external lookup. Null when
   * the address was private, unresolvable, or the database was unavailable.
   */
  location: string | null;
  signedInAt: string;
  lastActiveAt: string;
  /** True for the session making this request. */
  current: boolean;
  /** EMPTY until a consuming app introspects. Never rendered as "0 apps". */
  apps: readonly SessionAppSeen[];
}

/** GET /api/auth/me/sessions — the signed-in person's own devices. Never anyone else's. */
export interface MySessionsResponse {
  sessions: readonly SessionEntry[];
}

export interface AdminSessionGroup {
  /** Ids only — identity is a foreign key; name and email are labels for display. */
  user: { id: string; name: string; email: string };
  sessions: readonly SessionEntry[];
}

/** GET /api/admin/sessions — the whole live set, grouped by person. */
export interface AdminSessionsResponse {
  groups: readonly AdminSessionGroup[];
}

/* --------------------------- redirect notice ------------------------------ */

/**
 * GET /api/auth/redirect-target. Lets the SIGNED-OUT sign-in page name where it is about
 * to send somebody, without the client ever deciding whether that destination is legitimate.
 * Carries a display name and NOTHING else — no id, no key, no base URL — so it cannot
 * become a directory of the suite's internals.
 */
export interface RedirectTargetResponse {
  /** Null when the target is unregistered, inactive, or does not parse. */
  application: { name: string } | null;
}

/* --------------------------------- admin --------------------------------- */

export interface CreateUserRequest {
  email: string;
  name: string;
  isAdmin?: boolean;
  /** Display-only directory metadata. Empty string normalises to null (not set). */
  title?: string;
  /** Display-only directory metadata. Empty string normalises to null (not set). */
  department?: string;
}

/**
 * The ONE sanctioned surface for a generated temporary password: the direct response to
 * the call that created it. Shown once, never logged, never stored in plaintext,
 * never returned again.
 */
export interface CreateUserResponse {
  user: UserPublic;
  temporaryPassword: string;
}

export interface UserResponse {
  user: UserPublic;
}

/**
 * Body of PATCH /api/admin/users/:id. All fields optional, at least one required.
 * `isAdmin` routes through the same guarded service as POST :id/admin — the
 * last-active-admin guard applies; self-demotion is allowed by operator ruling.
 * An email change re-labels the SAME identity: everything keys on the uuid, so grants,
 * sessions and consuming-app links are untouched.
 */
export interface UpdateUserRequest {
  name?: string;
  email?: string;
  /** Display-only directory metadata. Empty string clears the field (stores null). */
  title?: string;
  /** Display-only directory metadata. Empty string clears the field (stores null). */
  department?: string;
  isAdmin?: boolean;
}

export interface ResetPasswordResponse {
  user: UserPublic;
  temporaryPassword: string;
}

export interface CreateApplicationRequest {
  key: string;
  name: string;
  baseUrl: string;
  /** Optional tile copy, max 280 after trim. Empty string (or absent) means "not set". */
  description?: string | null;
  /**
   * The two tag slots. Each must reference an EXISTING tag; a RETIRED tag is refused for
   * a new assignment; the two slots must never hold the SAME tag (two nulls are fine).
   */
  tag1Id?: string | null;
  tag2Id?: string | null;
}

/** `key` is deliberately absent: an application's key is immutable after create. */
export interface UpdateApplicationRequest {
  name?: string;
  baseUrl?: string;
  active?: boolean;
  /** Empty string clears the field (stores null); explicit null clears it too. */
  description?: string | null;
  /**
   * Explicit null clears a slot. A RETIRED tag is refused for a NEW or CHANGED
   * assignment; an UNCHANGED assignment of an already-retired tag passes untouched — an
   * admin is never forced to re-tag an application just to edit its description.
   */
  tag1Id?: string | null;
  tag2Id?: string | null;
  /**
   * The application's pill colour: a swatch NAME from ANNOUNCEMENT_SWATCHES (free hex is
   * refused server-side, exactly like a type's colour). Explicit null clears it.
   */
  pillColor?: AnnouncementSwatch | null;
}

export interface ApplicationResponse {
  application: ApplicationPublic;
}

/** Response of POST /api/admin/applications/:id/credentials. */
export interface IssueApplicationCredentialResponse {
  application: ApplicationPublic;
  credentialId: string;
  /**
   * THE ONE SANCTIONED SURFACE for the plaintext secret: the direct response to the call
   * that created it. Shown once, never logged, never stored in plaintext, never returned
   * again by any endpoint.
   */
  secret: string;
}

/** Body of POST /api/admin/applications/:id/test-connection. */
export interface TestApplicationConnectionRequest {
  /** The secret the operator configured in the consuming app. Never logged, never audited,
   *  never echoed back in any response field. */
  secret: string;
}

export type TestConnectionOutcome =
  /** The credential authenticated and introspection answered as it will for the real app. */
  | 'credential_ok'
  /** The secret matches no credential ever issued for this application. */
  | 'secret_not_recognised'
  /** The secret matches a credential that has since been revoked. */
  | 'secret_revoked'
  /** The credential is good, but the application is switched off suite-wide. */
  | 'application_inactive'
  /** This application has no active credential at all — issue one first. */
  | 'no_credential_issued';

export interface TestApplicationConnectionResponse {
  outcome: TestConnectionOutcome;
  /** Whether the credential itself is usable. Derived from `outcome`, never sent separately. */
  ok: boolean;
  /** A full sentence for a non-technical administrator. Never a code, never a raw error. */
  message: string;
}

/* --------------------------- application tags ----------------------------- */

/**
 * A managed application tag — a NEUTRAL label applications reference by id in their two
 * tag slots. NO colour, ever, by ruling. RETIRE, NEVER DELETE — and unlike announcement
 * types, tags are RESTORABLE, so a retired tag may also be renamed (see the server's
 * tags-service for why that asymmetry with D77 is deliberate).
 */
export interface ApplicationTagPublic {
  id: string;
  name: string;
  /** ISO timestamp; null while live. A retired tag stays on applications carrying it. */
  retiredAt: string | null;
  /** How many applications currently carry this tag in EITHER slot. */
  usedOnCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateApplicationTagRequest {
  name: string;
}

/** Rename. Allowed on retired tags too (they are restorable, unlike announcement types). */
export interface UpdateApplicationTagRequest {
  name: string;
}

export interface ApplicationTagResponse {
  tag: ApplicationTagPublic;
}

/** GET /api/admin/application-tags — every tag, live and retired. Unpaginated: small set. */
export interface ApplicationTagListResponse {
  data: ApplicationTagPublic[];
  meta: { total: number };
}

/* --------------------------------- grants --------------------------------- */

export interface CreateGrantRequest {
  userId: string;
  applicationId: string;
}

/** Body of POST /api/admin/grants/bulk: grant one application to every active user. */
export interface BulkGrantRequest {
  applicationId: string;
}

export interface BulkGrantResponse {
  /** Grants actually created by this call. */
  granted: number;
  /** Active users who already held a live grant — a no-op, never a duplicate or an error. */
  alreadyHeld: number;
  /** Active users considered. `granted + alreadyHeld === considered`. */
  considered: number;
}

export interface GrantResponse {
  grant: GrantPublic;
}

/**
 * GET /api/admin/grants. Not paginated (the query is always scoped to one user or one
 * application), so `meta` carries no limit/offset: `total` is the count matching the
 * current filters, `summary` the collection-wide live/revoked split.
 */
export interface GrantListResponse {
  data: GrantPublic[];
  meta: { total: number; summary: GrantsSummary };
}

/** Body of POST /api/admin/users/:id/admin — the only role this application holds. */
export interface SetAdminRequest {
  isAdmin: boolean;
}

/* --------------------------------- audit ---------------------------------- */

/**
 * One activity-log entry as the admin audit read surface returns it. The embedded `actor`
 * is resolved by ID JOIN — never by label — and is null for operator-CLI and anonymous
 * rows (there is no user behind either).
 */
export interface AuditEntryPublic {
  id: string;
  actorId: string | null;
  /** 'user' | 'operator_cli' | 'anonymous' — free text by design. */
  actorType: string;
  /** Dotted verb, e.g. `users.create`. */
  action: string;
  targetType: string | null;
  targetId: string | null;
  /** Audit metadata verbatim. Safe only because writes refuse secret-shaped keys. */
  metadata: Record<string, unknown> | null;
  createdAt: string;
  actor: { id: string; email: string; name: string } | null;
  /**
   * The source address the action arrived from, recorded at write time from the SAME
   * `request.ip` the rate limiters key on — never re-derived from a forwarded header.
   *
   * Null is a real and permanent answer, never a placeholder to fill in later:
   *   - operator-CLI rows have no request and therefore no address;
   *   - rows written before the address was captured have none and are NEVER backfilled,
   *     because a guessed address in an audit log is worse than an absent one.
   * Admin-gated surface only. This never reaches a consuming app: introspection does not
   * carry it, and nothing here is part of that contract.
   */
  ip: string | null;
}

/**
 * The audit screen's action-type filter. A GROUP, not a prefix: `apps` spans two verb
 * namespaces, `operator` is an actor-type predicate rather than an action at all, and
 * `sessions` is four exact verbs across two namespaces — none of them expressible as the
 * single `action` prefix the API already takes.
 *
 * Declared here and resolved SERVER-SIDE so the screen and the operator CLI can never
 * disagree about what counts as a security event — classifying a new verb is one edit in
 * one place. `sessions` is exactly `auth.login`, `auth.logout`, `auth.password_change`
 * and `seed.admin_create` — the routine sign-in lifecycle, previously reachable only
 * through "All actions" (the findability gap D32 recorded). It NEVER includes
 * `auth.login_failed` or `auth.login_rate_limited`: those two failure verbs are
 * `security`'s exact membership (D32), and the two groups must never overlap.
 *
 * `settings` was keyed `branding` until Phase 3 Wave 3A. The filter behind it was ALWAYS
 * `settings.*` — the name was stuck from when branding was the only thing Settings held,
 * and every operational section added since makes it wronger. Renamed while this export
 * still has no consumer outside this repo; `group` is a query-time filter, never a stored
 * column, so no `activity_log` row and no historical data changed (D32, amended).
 */
/*
 * `announcements` was added in Phase 3 Wave 3B, in the same breath as the verbs it matches
 * — deliberately, rather than shipping them ungrouped and reachable only through "All
 * actions". That is the findability gap D32 recorded as accepted and D44 later had to
 * close for the session verbs; closing it at birth here costs one line and spares a
 * second amendment.
 */
export const AUDIT_GROUPS = [
  'users',
  'apps',
  'grants',
  'announcements',
  'sessions',
  'security',
  'operator',
  'settings',
  // Access wave 1 (SIR-7460 plan 3d), added in the same edit as the verbs they match.
  'access',
  'requests',
  'lifecycle',
  // Access wave 2 (SIR-7460 plan 3d): the app-deactivation sync verbs, with the feature.
  'deprovisioning',
] as const;

export type AuditGroup = (typeof AUDIT_GROUPS)[number];

/**
 * GET /api/admin/audit/export — the WHOLE filtered set in one response, bounded by the
 * server-side export cap. When the matching set exceeds the cap the endpoint REFUSES with
 * `export_too_large` (both numbers named in the message) rather than truncating: a partial
 * export that looks whole is worse than no export. `meta.total` therefore always equals
 * `data.length` on a 200. The client formats the CSV; the server provides rows and the cap.
 */
export interface AuditExportResponse {
  data: AuditEntryPublic[];
  meta: { total: number };
}

/* ----------------------------- announcements ------------------------------ */

/**
 * The chip token families an announcement colour may name. Each value IS the CSS class
 * suffix of a chip family that already exists in web/src/styles.css (`.c-amber` …
 * `.c-slate`), themed across all four palette x mode combinations — the stored value is
 * the class suffix, no mapping layer. A hex value never enters the server, the database,
 * or the wire. ONE home for the set: the server validates both the type colour and the
 * per-application colour against exactly this array.
 *
 * Replaced the original eight semantic names (accent/info/success/warn/caution/danger/
 * prep/neutral) by operator ruling (reopening D80): those names had NO CSS class anywhere
 * and were literally unrenderable, while these ten families were already fully themed.
 * The swap was free — no `announcement_types` row and no `announcements.app_color.*`
 * settings row existed, the seed writes neither, and the backend had never been deployed.
 *
 * A runtime const in a type-only-by-design file, following the AUDIT_GROUPS precedent:
 * the value exists so the server's zod enum and this type can never drift.
 */
export const ANNOUNCEMENT_SWATCHES = [
  'amber',
  'blue',
  'emerald',
  'red',
  'orange',
  'violet',
  'indigo',
  'fuchsia',
  'rose',
  'slate',
] as const;

export type AnnouncementSwatch = (typeof ANNOUNCEMENT_SWATCHES)[number];

/**
 * Schedule state, DERIVED from `startsAt`/`endsAt` — never stored. The window is
 * HALF-OPEN [startsAt, endsAt): at exactly startsAt an announcement is LIVE; at exactly
 * endsAt it is ENDED. The server ships the derivation (`scheduleState`); no client ever
 * re-derives it.
 */
export type AnnouncementScheduleState = 'scheduled' | 'live' | 'ended';

/** A managed announcement type. Retired (`retiredAt`), never deleted. */
export interface AnnouncementTypePublic {
  id: string;
  name: string;
  color: AnnouncementSwatch;
  /** ISO timestamp; null while live. Retired types render forever on history. */
  retiredAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** An announcement as the admin panel sees one. */
export interface AnnouncementPublic {
  id: string;
  title: string | null;
  body: string;
  /** DERIVED from `body` by the server-side markdown renderer — never stored, never input. */
  bodyHtml: string;
  createdBy: string;
  active: boolean;
  /** Null = untargeted = suite-wide, always. IDENTITY IS A FOREIGN KEY: an id, never a key or name. */
  applicationId: string | null;
  /**
   * Resolved by ID JOIN. Null exactly when `applicationId` is null. `color` is the
   * application's pill colour (`applications.pill_color`), null when unassigned — resolved
   * here so the admin list's chips read the same one home every other surface reads.
   */
  application: { id: string; key: string; name: string; active: boolean; color: AnnouncementSwatch | null } | null;
  /** Meaningful only when targeted: true widens a targeted announcement to suite-wide. */
  showEveryone: boolean;
  /**
   * Nullable FOREVER on reads: type is required on WRITES only (D101), so pre-existing
   * untyped rows keep listing and rendering exactly as they always have.
   */
  typeId: string | null;
  /** Resolved by ID JOIN; resolves even when the type is retired (history renders forever). */
  type: AnnouncementTypePublic | null;
  startsAt: string | null;
  endsAt: string | null;
  /** Derived SERVER-SIDE — the one derivation. Half-open window [startsAt, endsAt). */
  scheduleState: AnnouncementScheduleState;
  createdAt: string;
  updatedAt: string;
  /** Resolved by ID JOIN, never by label. Null only if the author row is unreadable. */
  author: { id: string; email: string; name: string } | null;
}

/** One announcement as the LAUNCHER shows it. Deliberately carries no author identity,
 *  no `active` (an inactive announcement never appears), no schedule fields (the launcher
 *  only ever returns live ones), and no ids beyond its own and its joined labels'. */
export interface LauncherAnnouncement {
  id: string;
  title: string | null;
  body: string;
  /** DERIVED from `body` by the server-side markdown renderer — never stored, never input. */
  bodyHtml: string;
  /** The announcement's type, by ID JOIN; null on a pre-existing untyped row (D101 governs
   *  writes, not history — this path still has rows to serve). */
  type: { id: string; name: string; color: AnnouncementSwatch } | null;
  /**
   * Present ONLY on a targeted announcement the caller is entitled to see; null on an
   * untargeted one. `color` is the application's pill colour (`applications.pill_color`),
   * null when unassigned. Deliberately carries NO key, NO baseUrl, NO active flag — the
   * launcher shape stays minimal and non-enumerable.
   */
  application: { id: string; name: string; color: AnnouncementSwatch | null } | null;
  createdAt: string;
}

export interface AnnouncementResponse {
  announcement: AnnouncementPublic;
}

/** GET /api/admin/announcements. Unpaginated: the set is inherently small. */
export interface AnnouncementListResponse {
  data: AnnouncementPublic[];
  meta: { total: number };
}

/** GET /api/me/announcements. ACTIVE announcements only, newest first. */
export interface LauncherAnnouncementsResponse {
  data: LauncherAnnouncement[];
  /**
   * How many rendered LINES of a body the launcher shows before "View more", resolved
   * server-side from the live `announcement_clamp_lines` setting (bounds 1..8, default 3).
   *
   * It rides THIS read rather than a separate one for the reason `layout` rides the
   * applications read: the cards it governs cannot render before this response exists, so
   * /web never needs — and never carries — a copy of the shipped default. ONE FACT, ONE
   * HOME: the literal 3 lives in the server's settings module and nowhere else.
   */
  clampLines: number;
}

export interface CreateAnnouncementRequest {
  /** Optional per spec — an announcement may be body-only. Empty string means "not set". */
  title?: string;
  body: string;
  /** Null (or absent) = untargeted = suite-wide. Targeting an inactive application is refused. */
  applicationId?: string | null;
  /** Meaningful only when targeted: true makes a targeted announcement suite-wide. */
  showEveryone?: boolean;
  /**
   * REQUIRED (D101): every announcement carries a type. Absent or null is refused (400
   * `validation_error`) at the server boundary, not by the form. A RETIRED type is still
   * refused (400 `inactive_target`).
   */
  typeId: string;
  /** ISO-8601 with offset, or null. Half-open window [startsAt, endsAt). */
  startsAt?: string | null;
  endsAt?: string | null;
}

/**
 * Body of PATCH /api/admin/announcements/:id. All fields optional, at least one required.
 * `createdBy` is deliberately absent: the author is who wrote it, and history is not
 * edited. An empty-string `title` clears the field (stores null). Explicit `null` clears
 * `applicationId` (back to suite-wide), `startsAt`, or `endsAt` — `typeId` is NOT
 * clearable (D101).
 */
export interface UpdateAnnouncementRequest {
  title?: string;
  body?: string;
  applicationId?: string | null;
  showEveryone?: boolean;
  /**
   * Omit to leave the standing type in place; null is refused (D101 — a type cannot be
   * cleared). A pre-existing UNTYPED announcement must be given a type before an edit can
   * save: history is not rewritten, but the edit is where the type gets supplied.
   */
  typeId?: string;
  startsAt?: string | null;
  endsAt?: string | null;
}

/* ------------------------- announcement types ----------------------------- */

export interface CreateAnnouncementTypeRequest {
  name: string;
  color: AnnouncementSwatch;
}

/** Rename and/or recolor — at least one field required. Refused on a retired type. */
export interface UpdateAnnouncementTypeRequest {
  name?: string;
  color?: AnnouncementSwatch;
}

export interface AnnouncementTypeResponse {
  type: AnnouncementTypePublic;
}

/** GET /api/admin/announcement-types — every type, live and retired. Unpaginated: small set. */
export interface AnnouncementTypeListResponse {
  data: AnnouncementTypePublic[];
  meta: { total: number };
}

/* ---------------------- application pill colour (moved) -------------------- */

/*
 * The whole-section colour endpoints (GET/PUT /api/admin/settings/announcements) are GONE
 * (U2, operator ruling): an application's colour is now `ApplicationPublic.pillColor`, a
 * first-class column on the application row, written through the application PATCH on its
 * own detail pane — ONE APPLICATION, ONE PLACE.
 *
 * The three shapes that described those endpoints lived here briefly, marked deprecated,
 * only so /web could keep compiling while it was rewired in the same round. /web no longer
 * imports them and nothing else ever did, so they are gone: a contract surface describing
 * an endpoint that does not exist is a lie the next reader has to disprove.
 */

/* -------------------------------- branding -------------------------------- */

/**
 * Product presentation, exposed WITHOUT a credential (the sign-in page renders it while
 * signed out). No user data, no counts, nothing enumerable — presentation only.
 */
export interface BrandingPublic {
  name: string;
  tagline: string;
  taglineSub: string;
  glyph: string;
  /**
   * `/api/branding/logo?v=<token>` when a logo is set, otherwise null. The token derives
   * from the logo pointer's updated-at, so replacing the logo changes the URL and the
   * browser re-requests instead of serving its cache.
   */
  logoUrl: string | null;
  defaultPalette: 'default' | 'sage';
  defaultMode: 'light' | 'dark';
}

export interface BrandingResponse {
  branding: BrandingPublic;
}

/** Body of PUT /api/admin/settings/branding. */
export interface UpdateBrandingRequest {
  name: string;
  tagline: string;
  taglineSub: string;
  glyph: string;
}

/* --------------------------- operational settings -------------------------- */

/**
 * Operational settings an operator tunes from the admin panel. Read LIVE from the
 * `settings` table on every use — never cached at boot, so a change takes effect on the
 * next poll cycle rather than the next restart.
 */
export interface OperationalSettingsPublic {
  /** How often the health poller sweeps every ACTIVE application, in seconds. Bounds 15..3600. */
  healthPollIntervalSeconds: number;
  /** Consecutive failed polls before an application is reported unreachable. Bounds 1..10. */
  healthPollFailureThreshold: number;
  /** Session cookie lifetime, in DAYS, read at every cookie issue. Bounds 1..365. */
  sessionTtlDays: number;
  /**
   * How long a consuming app may cache an ACTIVE introspection verdict, in SECONDS —
   * echoed as `cacheTtlSeconds` on every active verdict. This IS the suite's
   * deprovisioning window (D7/D24): the worst-case time a revoked person keeps working
   * inside a consuming application. Bounds 30..3600.
   */
  introspectionCacheTtlSeconds: number;
  /**
   * Rows per page on the admin Audit log screen. Bounds 10..200 — the upper bound is the
   * audit list endpoint's own `limit` ceiling, so a page size can never be set to
   * something the endpoint would refuse.
   *
   * A SCREEN preference, not an API or CLI default: `GET /api/admin/audit` keeps its own
   * `limit` default of 50, and the operator CLI's `list-audit --limit` keeps its own, so
   * changing this can never alter what a scripted call returns.
   */
  auditPageSize: number;
  /**
   * How long a DEAD session-inventory row (ended, or past its recorded expiry) is kept
   * before the operator's `purge-sessions` sweep removes it, in DAYS. Read LIVE at every
   * purge execution. Bounds 1..365; default 30, unseeded. This is also the PER-DEVICE
   * REACH window: per-device sign-out resolves its target through the inventory row, so a
   * purged session can be ended only by sign-out-everywhere or deactivation. Revocations
   * are never purged, whatever this value.
   */
  sessionPurgeDays: number;
  /**
   * TOTP clock-drift tolerance, in 30-second steps either side of now, read LIVE at every
   * challenge verification. Bounds 0..2 — DELIBERATELY capped at 2: each step widens the
   * window in which any one code verifies, so beyond ±2 (≈90s of validity) this stops
   * being drift tolerance and becomes a replay window. The ceiling makes the hole
   * unconfigurable, not merely discouraged. Default 1, unseeded.
   */
  totpDriftSteps: number;
  /** The operator's 2FA requirement — see `TotpRequirement`. Default 'optional', unseeded. */
  totpRequirement: TotpRequirement;
  /**
   * How many rendered LINES of an announcement body the launcher shows before "View more".
   * Bounds 1..8; default 3, unseeded. Read LIVE on every launcher announcements read and
   * handed to the screen on that same read, so an operator change takes effect on the next
   * data read — no restart, no rebuild.
   *
   * A PRESENTATION setting, deliberately: it governs how much of a body the launcher shows,
   * never what the server stores or renders. The full text is always one click away, and the
   * markdown renderer is untouched by it.
   */
  announcementClampLines: number;
  /*
   * RATE LIMITS (U5). Six live-read settings, all unseeded; absent means the ENV value
   * (LOGIN_RATE_LIMIT_MAX etc.), which itself defaults to the shipped constant. Read LIVE
   * at every limiter decision — an operator change binds the very next request, no
   * restart. PER-INSTANCE, in-process counters: these bound ONE Accounts process and make
   * no suite-wide guarantee across instances (there is exactly one instance today; a
   * shared store is a registered future decision, deliberately not built).
   */
  /** Failed sign-in ceiling per EMAIL per window (the per-IP ceiling is 5x this). Bounds 1..10000. */
  loginRateLimitMax: number;
  /** The login counters' window, in seconds. Bounds 1..86400. */
  loginRateLimitWindowSeconds: number;
  /** Successfully-authenticated introspection calls per APPLICATION per window. Bounds 1..100000. */
  introspectRateLimitMax: number;
  /** The per-application introspection window, in seconds. Bounds 1..86400. */
  introspectRateLimitWindowSeconds: number;
  /** FAILED introspection caller-authentications per SOURCE IP per window — the spray guard. Bounds 1..10000. */
  introspectFailureRateLimitMax: number;
  /** The failure spray guard's window, in seconds. Bounds 1..86400. */
  introspectFailureRateLimitWindowSeconds: number;
}

export interface OperationalSettingsResponse {
  settings: OperationalSettingsPublic;
}

/** Body of PUT /api/admin/settings/operational. ALL fields required — the screen saves the section as a unit. */
export interface UpdateOperationalSettingsRequest {
  healthPollIntervalSeconds: number;
  healthPollFailureThreshold: number;
  sessionTtlDays: number;
  introspectionCacheTtlSeconds: number;
  auditPageSize: number;
  sessionPurgeDays: number;
  totpDriftSteps: number;
  totpRequirement: TotpRequirement;
  announcementClampLines: number;
  loginRateLimitMax: number;
  loginRateLimitWindowSeconds: number;
  introspectRateLimitMax: number;
  introspectRateLimitWindowSeconds: number;
  introspectFailureRateLimitMax: number;
  introspectFailureRateLimitWindowSeconds: number;
}

/* -------------------------------------------------------------------------- *
 * The introspection contract — POST /api/introspect.
 *
 * THIS SHAPE IS THE ARTIFACT CONSUMING APPS IMPORT. It is the single most
 * consequential contract in this package: every application in the suite builds
 * its session middleware against it. Changing any of it after any consuming app
 * depends on it is a SUITE-WIDE BREAKING CHANGE, never a refactor — bump
 * `IntrospectionContractVersion` and coordinate every consumer, or do not touch it.
 *
 * Like everything else in this file it is TYPE-ONLY: the contract version's
 * runtime value lives in /server, typed against the literal below, so the two can
 * never drift without a compile error.
 * -------------------------------------------------------------------------- */

/** The introspection contract's version. Bumping this is a suite-wide breaking change. */
export type IntrospectionContractVersion = 1;

/**
 * POST /api/introspect request body. The raw suite session cookie value (named
 * `accounts_session` unless the operator configured `SESSION_COOKIE_NAME`) the consuming
 * app received from the browser travels in the BODY — never a header, never a query
 * parameter — so it can never land in an access log.
 */
export interface IntrospectionRequest {
  /** The raw cookie value. Absent or empty is a legitimate call: the verdict is `no_session`. */
  session?: string;
}

/** Why a session was refused. One code per distinct mode, each meaningful to a consuming app. */
export type IntrospectionFailureCode =
  /** The consuming app received no session cookie (or an empty one) from the browser. */
  | 'no_session'
  /** The value did not verify against this service's signing secret — forged, truncated, or from another deployment. */
  | 'bad_signature'
  /** Validly signed, but the payload names a different credential lane than the full session. */
  | 'wrong_lane'
  /** The session predates a `session_version` bump: signed out everywhere, password changed, or deactivated. */
  | 'stale_session_version'
  /** The account is deactivated. Distinct from `no_grant` so the app can say which. */
  | 'user_inactive'
  /** The session is otherwise valid, but its holder must finish changing their password in Accounts before they may enter anything. */
  | 'password_change_required'
  /** The session is valid; this person holds no live grant for the calling application. */
  | 'no_grant'
  /** The calling application is deactivated suite-wide. Nobody enters, grants notwithstanding. */
  | 'application_inactive';

/** Never carries `is_admin`, and never a role. Suite-admin is Accounts-internal (D0/D1). */
export interface IntrospectionUser { id: string; email: string; name: string }

export interface IntrospectionActiveResponse {
  version: IntrospectionContractVersion;
  active: true;
  user: IntrospectionUser;
  /** How long the consuming app may cache this verdict. THE deprovisioning window (D7). */
  cacheTtlSeconds: number;
}

/** Carries NO user information at all — not even on `no_grant`, where a real person exists. */
export interface IntrospectionInactiveResponse {
  version: IntrospectionContractVersion;
  active: false;
  code: IntrospectionFailureCode;
}

export type IntrospectionResponse = IntrospectionActiveResponse | IntrospectionInactiveResponse;

/* ----------------------------- access (wave 1) ----------------------------- */
/*
 * SIR-7460 plan 3a. Accounts-internal admin surfaces only: nothing here is part of the
 * introspection payload, whose contract v1 is unchanged. Retire, never delete (D5): every
 * row carries `retiredAt` or ends in a terminal status (hierarchy levels are only added and
 * renamed). `login` is an email or username, NEVER a password: no field in these shapes
 * carries a password value. `passwordHandling` says only HOW one is handled.
 */

export const ACCESS_RECORD_STATUSES = [
  'active',
  'needs_access',
  'requested',
  'remove_access',
  'removed',
] as const;
export type AccessRecordStatus = (typeof ACCESS_RECORD_STATUSES)[number];

export const ACCESS_REQUEST_STATUSES = ['pending', 'approved', 'denied', 'cancelled'] as const;
export type AccessRequestStatus = (typeof ACCESS_REQUEST_STATUSES)[number];

export type AccessRequestSource = 'self' | 'onboarding' | 'on_behalf';

/** `admin` only today; the column is ready for `user` / `role` approvers later. */
export type AccessApproverKind = 'admin';

/**
 * How the password of an OUTSIDE-system login is handled (board, 18 Oct):
 *   - `managed`: we hold and save it (VM / saved for them); the person never sees it;
 *   - `given_raw`: the person was told the password.
 * Offboarding (wave 2) reads it: rotate only for an outside system, `given_raw`, and a
 * departing person who had that login; `managed` means "Remove VM access" instead.
 */
export const ACCESS_PASSWORD_HANDLINGS = ['managed', 'given_raw'] as const;
export type AccessPasswordHandling = (typeof ACCESS_PASSWORD_HANDLINGS)[number];

/**
 * The configurable access hierarchy (board decision of 18 Oct, amending SIR-7460
 * decision 2): admin-named levels ordered by `depth` (1 = top), and a tree of nodes.
 */
export interface AccessLevelPublic {
  id: string;
  name: string;
  depth: number;
  createdAt: string;
  updatedAt: string;
}

/** One step of a node's path, root first. */
export interface AccessNodePathEntry {
  id: string;
  name: string;
  level: { id: string; name: string };
  retired: boolean;
}

export interface AccessNodePublic {
  id: string;
  name: string;
  /** Null = a root node. */
  parentId: string | null;
  level: { id: string; name: string; depth: number };
  /** Root → this node, inclusive. */
  path: AccessNodePathEntry[];
  retiredAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AccessCatalogItemPublic {
  id: string;
  name: string;
  /** Where the row sits in the hierarchy (any node, any level); null = not placed. */
  nodeId: string | null;
  /** Root → the row's node; empty when not placed. */
  path: AccessNodePathEntry[];
  /** The Connected app. Null = "None (outside system)": access is an access record. */
  application: { id: string; key: string; name: string } | null;
  requestable: boolean;
  approverKind: AccessApproverKind;
  hrCanGrant: boolean;
  retiredAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AccessUserRef {
  id: string;
  email: string;
  name: string;
}

export interface AccessRecordPublic {
  id: string;
  user: AccessUserRef;
  catalogItem: { id: string; name: string };
  /** Root → the catalog row's node; empty when the row is not placed. */
  path: AccessNodePathEntry[];
  login: string | null;
  caseLogin: string | null;
  status: AccessRecordStatus;
  passwordHandling: AccessPasswordHandling;
  notes: string | null;
  addedBy: string;
  addedAt: string;
  statusChangedAt: string;
  removedBy: string | null;
  removedAt: string | null;
  retiredAt: string | null;
  /**
   * DERIVED, never stored: the other people with a live record on the same item and the
   * same login (case-insensitive). Empty when the login is not shared.
   */
  sharedWith: AccessUserRef[];
}

export interface AccessRequestPublic {
  id: string;
  user: AccessUserRef;
  catalogItem: { id: string; name: string; applicationId: string | null; hrCanGrant: boolean };
  requestedBy: string;
  source: AccessRequestSource;
  note: string | null;
  status: AccessRequestStatus;
  decidedBy: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  createdAt: string;
}

export interface AccessLevelListResponse { data: AccessLevelPublic[] }
export interface AccessLevelResponse { level: AccessLevelPublic }
export interface AccessNodeListResponse { data: AccessNodePublic[] }
export interface AccessNodeResponse { node: AccessNodePublic }
export interface AccessCatalogListResponse { data: AccessCatalogItemPublic[] }
export interface AccessCatalogItemResponse { item: AccessCatalogItemPublic }
export interface AccessRecordListResponse { data: AccessRecordPublic[] }
export interface AccessRecordResponse { record: AccessRecordPublic }
export interface AccessRequestListResponse { data: AccessRequestPublic[] }
export interface AccessRequestResponse { request: AccessRequestPublic }

/** Approving an app-bound request creates a grant; an outside one, an access record. */
export interface AccessRequestDecisionResponse {
  request: AccessRequestPublic;
  grantId: string | null;
  recordId: string | null;
}

export const ACCESS_BULK_ACTIONS = ['approve', 'deny', 'mark_granted', 'mark_removed'] as const;
export type AccessBulkAction = (typeof ACCESS_BULK_ACTIONS)[number];

export interface AccessBulkResponse {
  bulkId: string;
  action: AccessBulkAction;
  /** Items changed; one audit row each, all sharing `bulkId`. */
  affected: number;
}

/** Queue item types: a pending request, or an access record waiting on someone. */
export const ACCESS_QUEUE_TYPES = ['request', 'needs_access', 'requested', 'remove_access'] as const;
export type AccessQueueType = (typeof ACCESS_QUEUE_TYPES)[number];

export interface AccessQueueItemPublic {
  type: AccessQueueType;
  /** The request id for `request`, the access-record id otherwise. */
  id: string;
  user: AccessUserRef;
  /** `hrCanGrant`: the row is in HR's scope (HR's Queue holds only such rows). */
  catalogItem: { id: string; name: string; applicationId: string | null; hrCanGrant: boolean };
  /** Request provenance; null for record-backed queue rows. */
  source: AccessRequestSource | null;
  /** The requester's note; null for record-backed queue rows or when none was supplied. */
  note: string | null;
  /** Root → the catalog row's node; empty when the row is not placed. */
  path: AccessNodePathEntry[];
  login: string | null;
  /** When the item entered the queue: request creation, or the record's last status change. */
  since: string;
  overdue: boolean;
  /** Derived co-users of a shared login (records only); never stored. */
  sharedWith: AccessUserRef[];
}

export interface AccessQueueResponse {
  data: AccessQueueItemPublic[];
  /**
   * Wave 2, ADDED beside `data` (whose item shape is unchanged): open app deactivations —
   * outbox rows waiting for their application to confirm — oldest first. Administrators
   * only: always empty for HR (plan 3b point 7; HR's Queue never holds app deactivations).
   * Filtered by `userId` and `q`; empty when `type`, `status` or `nodeId` names a lane of
   * the other kind.
   */
  deactivations: AccessDeactivationQueueItem[];
  meta: {
    total: number;
    /** Counts per type over the caller's WHOLE queue, independent of the filters. */
    counts: Record<AccessQueueType, number>;
    overdueAfterDays: number;
    /** Wave 2: open / overdue app deactivations over the caller's whole queue. */
    deactivationCounts: { open: number; overdue: number };
    /** Wave 2: the live `deprovision_overdue_hours` setting the `overdue` flags used. */
    deprovisionOverdueHours: number;
  };
}

/* ------------------------------------------------------------- access (wave 2) -- */
/*
 * SIR-7460 plan 3a / 3b / 5.4. Onboarding and offboarding checklists, the IT email draft,
 * and the app-deactivation outbox (deprovisioning v1). Accounts-internal admin surfaces,
 * except the two deprovisioning v1 endpoints a consuming app calls with its existing
 * credential. Introspection contract v1 is unchanged.
 */

export const DEPROVISIONING_REASONS = ['grant_revoked', 'user_deactivated'] as const;
export type DeprovisioningReason = (typeof DEPROVISIONING_REASONS)[number];

/** 'rung' = the doorbell got any 2xx; 'failed' = anything else. Null before the first ring. */
export type DoorbellResult = 'rung' | 'failed';

/** One open outbox row, as the Queue shows it (administrators only). */
export interface AccessDeactivationQueueItem {
  /** The outbox row id (the id the application confirms). */
  id: string;
  user: AccessUserRef;
  application: { id: string; key: string; name: string; active: boolean };
  reason: DeprovisioningReason;
  createdAt: string;
  /** Older than `deprovision_overdue_hours` and still not confirmed. */
  overdue: boolean;
  doorbell: {
    attempts: number;
    lastAt: string | null;
    nextAt: string;
    lastResult: DoorbellResult | null;
  };
}

export const CHECKLIST_KINDS = ['onboarding', 'offboarding'] as const;
export type ChecklistKind = (typeof CHECKLIST_KINDS)[number];

/**
 * What a checklist item asks for. Outside systems: `set_up` (onboarding, or an approved
 * outside request), and on offboarding one of the four removal choices — `remove`
 * (remove the account), `remove_vm_access` (Managed logins: take the login out of the
 * person's VM profile; the account stays for its co-users), `rotate_shared_password`
 * (Given raw shared logins: change the password, keep the account) and
 * `remove_from_shared_mailbox` (shared email logins). Suite apps: `app_deactivation`,
 * which ticks ONLY when the application confirms (deprovisioning v1).
 */
export const CHECKLIST_ACTIONS = [
  'set_up',
  'remove',
  'remove_vm_access',
  'rotate_shared_password',
  'remove_from_shared_mailbox',
  'app_deactivation',
] as const;
export type ChecklistAction = (typeof CHECKLIST_ACTIONS)[number];

/** The choices offboarding offers for one outside-system login. */
export const OFFBOARDING_RECORD_ACTIONS = [
  'remove',
  'remove_vm_access',
  'rotate_shared_password',
  'remove_from_shared_mailbox',
] as const;
export type OffboardingRecordAction = (typeof OFFBOARDING_RECORD_ACTIONS)[number];

export type ChecklistItemStatus = 'open' | 'done';

export interface ChecklistItemPublic {
  id: string;
  checklistId: string;
  action: ChecklistAction;
  status: ChecklistItemStatus;
  note: string | null;
  /** Null while open, and on an item an application confirmed (no user did it). */
  doneBy: string | null;
  doneAt: string | null;
  retiredAt: string | null;
  createdAt: string;
  /** The outside-system record the item is about; null for `app_deactivation`. */
  record: AccessRecordPublic | null;
  /** The suite application for `app_deactivation`; null otherwise. */
  application: { id: string; key: string; name: string } | null;
  /** `app_deactivation` only: the latest outbox row for this person and application. */
  deprovisioning: {
    outboxId: string;
    confirmedAt: string | null;
    supersededAt: string | null;
    overdue: boolean;
    doorbell: { attempts: number; lastAt: string | null; lastResult: DoorbellResult | null };
  } | null;
}

export interface LifecycleChecklistPublic {
  id: string;
  kind: ChecklistKind;
  user: AccessUserRef;
  createdBy: string;
  createdAt: string;
  completedAt: string | null;
  retiredAt: string | null;
  /** Live items not yet done. Zero on a completed checklist. */
  openCount: number;
  /** Live and retired items, oldest first. */
  items: ChecklistItemPublic[];
}

export interface LifecycleChecklistResponse { checklist: LifecycleChecklistPublic }
export interface LifecycleChecklistListResponse { data: LifecycleChecklistPublic[] }

/** One starting-access pick on POST /api/admin/lifecycle/onboarding. */
export interface OnboardingPick {
  catalogItemId: string;
  /**
   * `direct`: give it now (a grant for a connected app; an access record at `needs_access`
   * plus a `set_up` item for an outside system). HR only on rows flagged "HR can grant
   * directly". `request`: an onboarding-source request for an administrator to decide.
   */
  mode: 'direct' | 'request';
  /** Outside systems, `direct` only. Never a password. */
  login?: string | null;
  caseLogin?: string | null;
  passwordHandling?: AccessPasswordHandling;
  notes?: string | null;
}

export interface OnboardingRequest {
  email: string;
  name: string;
  title?: string;
  department?: string;
  /** Administrators only; HR creates members only. */
  isAdmin?: boolean;
  picks: OnboardingPick[];
}

export interface OnboardingResponse {
  user: UserPublic;
  /** Shown exactly once, as on POST /api/admin/users. */
  temporaryPassword: string;
  checklist: LifecycleChecklistPublic;
  grantIds: string[];
  requestIds: string[];
  recordIds: string[];
  /** The Review step's three numbers. */
  counts: { givenNow: number; requestsToAdmin: number; itSetupItems: number };
}

/** One outside-system login in the offboarding preview. */
export interface OffboardingRecordPreview {
  record: AccessRecordPublic;
  /** Derived: other people hold the same login (`record.sharedWith` is non-empty). */
  shared: boolean;
  /** The choices allowed for this login, in display order. */
  choices: OffboardingRecordAction[];
  /** What offboarding does when no choice is sent for this login. */
  defaultChoice: OffboardingRecordAction;
}

/** GET /api/admin/users/:id/offboarding — step 1 and 2 of the Offboard flow. */
export interface OffboardingPreviewResponse {
  user: UserPublic;
  /** Suite apps: live grants, each revoked, and each application asked to deactivate. */
  suiteApps: { grantId: string; application: { id: string; key: string; name: string } }[];
  sharedLogins: OffboardingRecordPreview[];
  personalLogins: OffboardingRecordPreview[];
  /** An offboarding checklist already open for this person, if any (offboarding refuses). */
  openChecklistId: string | null;
}

export interface OffboardRequest {
  /** Per access-record id; a login with no entry gets its `defaultChoice`. */
  choices?: Record<string, OffboardingRecordAction>;
}

export interface OffboardResponse {
  user: UserPublic;
  checklist: LifecycleChecklistPublic;
  revokedGrants: number;
}

export interface TickChecklistItemRequest { note?: string | null }
export interface ChecklistItemResponse { item: ChecklistItemPublic; checklist: LifecycleChecklistPublic }

/** Access settings (admin). Read live. */
export interface AccessSettingsPublic {
  /** Open app deactivations older than this show as overdue. Bounds 1..168; default 24. */
  deprovisionOverdueHours: number;
  switches: AccessSwitchesPublic;
}
export interface AccessSettingsResponse { settings: AccessSettingsPublic }
export interface UpdateAccessSettingsRequest {
  deprovisionOverdueHours: number;
  switches: AccessSwitchesPublic;
}

/* -------------------------------------------------------------------------- *
 * Deprovisioning v1 — the consuming-app pull surface (plan 3b). A SEPARATELY VERSIONED
 * contract from introspection v1 (which is unchanged). Same caller credential
 * (X-App-Key / X-App-Secret), same 401 codes and same 429 shape as introspection.
 * -------------------------------------------------------------------------- */

export type DeprovisioningContractVersion = 1;

export interface DeprovisioningPendingItem {
  /** The outbox row id; confirm it once the app-level flag is set. */
  id: string;
  suiteUserId: string;
  reason: DeprovisioningReason;
  createdAt: string;
}

/** GET /api/deprovisioning/v1/pending — only the calling application's open rows. */
export interface DeprovisioningPendingResponse {
  version: DeprovisioningContractVersion;
  data: DeprovisioningPendingItem[];
}

/**
 * POST /api/deprovisioning/v1/pending/:id/confirm. No body, or `{ via: 'backstop' }` when
 * the application had already set its flag because introspection answered `no_grant` /
 * `user_inactive` (backstop A). Another application's id, an unknown id or a superseded
 * row answer the same `404 not_found`.
 */
export interface DeprovisioningConfirmRequest { via?: 'backstop' }

export interface DeprovisioningConfirmResponse {
  version: DeprovisioningContractVersion;
  id: string;
  /** Idempotent: a repeat confirm answers the first confirm's time. */
  confirmedAt: string;
}
/* ---------------- access rows 13–15: Other, bell, mail and IT email ---------------- */

export const OTHER_ACCESS_REQUEST_STATUSES = ['pending', 'approved', 'denied', 'cancelled'] as const;
export type OtherAccessRequestStatus = (typeof OTHER_ACCESS_REQUEST_STATUSES)[number];

export interface OtherAccessRequestPublic {
  id: string;
  user: AccessUserRef;
  what: string;
  where: { nodeId: string; path: AccessNodePathEntry[] } | null;
  why: string;
  status: OtherAccessRequestStatus;
  catalogItem: { id: string; name: string } | null;
  decidedBy: AccessUserRef | null;
  decidedAt: string | null;
  declineReason: string | null;
  createdAt: string;
  overdue: boolean;
}

export interface CreateOtherAccessRequestRequest {
  what: string;
  nodeId?: string | null;
  why: string;
}

export interface OtherAccessRequestResponse { request: OtherAccessRequestPublic }
export interface OtherAccessRequestListResponse { data: OtherAccessRequestPublic[] }
export interface AdminOtherAccessRequestListResponse {
  data: OtherAccessRequestPublic[];
  meta: { total: number; pending: number; overdue: number; overdueAfterDays: number };
}

export interface ApproveOtherAccessRequestRequest {
  name: string;
  nodeId: string | null;
  applicationId: string | null;
  requestable: boolean;
  hrCanGrant: boolean;
  outsideSystem?: {
    login: string | null;
    passwordHandling: AccessPasswordHandling;
    status: 'needs_access' | 'active';
  };
}

export interface ApproveOtherAccessRequestResponse {
  request: OtherAccessRequestPublic;
  item: AccessCatalogItemPublic;
  grantId: string | null;
  recordId: string | null;
}

export interface DeclineOtherAccessRequestRequest { reason?: string | null }

export const ANSWER_EMAIL_STATUSES = ['pending', 'captured', 'sent', 'failed'] as const;
export type AnswerEmailStatus = (typeof ANSWER_EMAIL_STATUSES)[number];
export const ANSWER_EMAIL_KINDS = [
  'request_approved',
  'request_declined',
  'other_approved',
  'other_declined',
] as const;
export type AnswerEmailKind = (typeof ANSWER_EMAIL_KINDS)[number];

export interface AnswerEmailPublic {
  id: string;
  kind: AnswerEmailKind;
  to: string;
  subject: string;
  body: string;
  status: AnswerEmailStatus;
  createdAt: string;
  sentAt: string | null;
}

export interface AnswerEmailListResponse { data: AnswerEmailPublic[] }

export type MailDriver = 'capture' | 'sendgrid';

export interface AdminMailEmailPublic extends AnswerEmailPublic {
  driver: MailDriver;
  related: { type: 'access_request' | 'other_access_request'; id: string };
}

export interface AdminMailEmailListResponse {
  data: AdminMailEmailPublic[];
  meta: { total: number; page: number; pageSize: number };
}

export const BELL_NOTIFICATION_KINDS = [
  'request_submitted',
  'other_request_submitted',
  'request_answered',
  'other_request_answered',
] as const;
export type BellNotificationKind = (typeof BELL_NOTIFICATION_KINDS)[number];

/** One server-owned row in the admin Email & notifications event catalog. */
export interface EmailSettingsEventPublic {
  key: BellNotificationKind;
  label: string;
  emailEnabled: boolean;
  inAppEnabled: boolean;
}

export interface EmailSettingsPublic {
  masterEnabled: boolean;
  fromAddress: string;
  senderName: string;
  /** Null means use fromAddress. */
  replyTo: string | null;
  events: EmailSettingsEventPublic[];
}

export interface EmailSettingsStatusPublic {
  driver: MailDriver;
  sendGridApiKeyConfigured: boolean;
  /** Last four characters only. The key and encrypted payload never cross the API. */
  sendGridApiKeyLast4: string | null;
  masterEnabled: boolean;
  fromAddress: string;
}

export interface EmailSettingsResponse {
  settings: EmailSettingsPublic;
  status: EmailSettingsStatusPublic;
}

export type EmailTestSkipReason =
  | 'capture_mode'
  | 'master_disabled'
  | 'api_key_not_configured';

export type EmailTestResult =
  | { status: 'sent'; recipient: string }
  | { status: 'skipped'; recipient: string; reason: EmailTestSkipReason };

export interface EmailTestResponse {
  result: EmailTestResult;
}

export interface BellNotificationPublic {
  id: string;
  /** Stable compatibility key for the existing bell component. */
  key: string;
  kind: BellNotificationKind;
  title: string;
  body: string;
  at: string;
  link: string;
  readAt: string | null;
  related: { type: 'access_request' | 'other_access_request'; id: string };
  emailId: string | null;
}

export interface BellNotificationListResponse {
  data: BellNotificationPublic[];
  meta: { unreadCount: number; accessRequestCount: number };
}

export interface BellNotificationReadResponse {
  notification: BellNotificationPublic;
  meta: { unreadCount: number };
}

export interface BellNotificationReadAllResponse {
  markedRead: number;
  meta: { unreadCount: 0 };
}

export const IT_EMAIL_STATUSES = ['draft', 'sent', 'completed', 'taken_back'] as const;
export type ItEmailStatus = (typeof IT_EMAIL_STATUSES)[number];
export const IT_EMAIL_ACTIONS = ['set_up', 'remove'] as const;
export type ItEmailAction = (typeof IT_EMAIL_ACTIONS)[number];
export const IT_EMAIL_ITEM_STATUSES = ['open', 'granted', 'removed', 'taken_back'] as const;
export type ItEmailItemStatus = (typeof IT_EMAIL_ITEM_STATUSES)[number];

export interface ItEmailItemPublic {
  id: string;
  recordId: string;
  user: AccessUserRef;
  system: { id: string; name: string };
  login: string | null;
  passwordHandling: AccessPasswordHandling;
  action: ItEmailAction;
  status: ItEmailItemStatus;
  completedBy: AccessUserRef | null;
  completedAt: string | null;
}

export interface ItEmailPublic {
  id: string;
  status: ItEmailStatus;
  text: string;
  items: ItEmailItemPublic[];
  createdBy: AccessUserRef;
  createdAt: string;
  sentBy: AccessUserRef | null;
  sentAt: string | null;
  completedAt: string | null;
  takenBackBy: AccessUserRef | null;
  takenBackAt: string | null;
  openItemCount: number;
  overdue: boolean;
}

export interface ItEmailResponse { email: ItEmailPublic }
export interface ItEmailListResponse {
  data: ItEmailPublic[];
  meta: { total: number; overdueAfterDays: number };
}

export interface CreateItEmailRequest { recordIds: string[] }
export interface UpdateItEmailRequest { text: string }

export interface ItEmailMembershipPublic {
  recordId: string;
  emailId: string;
  emailStatus: 'draft' | 'sent';
  itemId: string;
  action: ItEmailAction;
}

export interface ItEmailMembershipListResponse { data: ItEmailMembershipPublic[] }
